/**
 * FrostedPowerMeterModal — Frosted Glass Power Meter
 *
 * 設計語言：
 * - Cyan/Electric Blue tint → 電力監控專用色調
 * - 自動偵測：單相 (single) vs 三相四線 (3-phase)
 * - 三相模式：Tab + Swipe 切換 總表/R/S/T
 * - SVG 圓弧 gauge 顯示即時功率
 * - Metrics: Voltage / Current / Power Factor
 * - Energy: Today / This Month
 * - Phase Breakdown 卡片（三相總表頁面）
 *
 * 完全自包含：parent 只需傳 deviceSN + credentials
 */
import { useState, useCallback, useMemo, useRef, useEffect } from 'react'
import type { DeviceModalTemplateProps, ModalSectionMeta } from '../types/deviceModalTemplate'
import { configureDeviceNicknameWithAuth } from '../services/turingMcpApi'
import './FrostedPowerMeterModal.css'

// ---------------------------------------------------------------------------
// Section definitions (exported for registry)
// ---------------------------------------------------------------------------

export const POWER_METER_FROSTED_SECTIONS: ModalSectionMeta[] = [
  { id: '_modal', label: 'Modal Background', icon: 'smartphone', slots: [] },
  { id: 'gauge', label: 'Power Gauge', icon: 'speed', slots: [
    { id: 'gauge-arc', label: 'Arc Stroke', editableProps: ['color', 'glow'] },
    { id: 'gauge-value', label: 'Watt Value', editableProps: ['fontSize', 'color'] },
  ]},
  { id: 'metrics', label: 'Metrics', icon: 'electric_bolt', slots: [
    { id: 'metric-card', label: 'Metric Card', editableProps: ['background', 'borderRadius', 'border'] },
  ]},
  { id: 'phases', label: 'Phase Breakdown', icon: 'account_tree', slots: [] },
  { id: 'energy', label: 'Energy', icon: 'energy_savings_leaf', slots: [] },
  { id: 'chart', label: 'Power Chart', icon: 'show_chart', slots: [] },
]

const DEFAULT_SECTION_ORDER = POWER_METER_FROSTED_SECTIONS.map((s) => s.id)

// ---------------------------------------------------------------------------
// Phase data key mapping
// ---------------------------------------------------------------------------

type PhaseId = 'total' | 'r' | 's' | 't'

const PHASE_KEYS: Record<PhaseId, { voltage: string; current: string; power: string; reactivePower: string; apparentPower: string }> = {
  total: { voltage: 'voltage',   current: 'current',   power: 'real_power',   reactivePower: 'reactive_power',   apparentPower: 'apparent_power' },
  r:     { voltage: 'voltage_r', current: 'current_r', power: 'real_power_r', reactivePower: 'reactive_power_r', apparentPower: 'apparent_power_r' },
  s:     { voltage: 'voltage_s', current: 'current_s', power: 'real_power_s', reactivePower: 'reactive_power_s', apparentPower: 'apparent_power_s' },
  t:     { voltage: 'voltage_t', current: 'current_t', power: 'real_power_t', reactivePower: 'reactive_power_t', apparentPower: 'apparent_power_t' },
}

const PHASE_COLORS: Record<PhaseId | 'single', { label: string; tabClass: string; dotClass: string; gaugeClass: string }> = {
  single: { label: 'Real-time Power', tabClass: '', dotClass: 'c-total', gaugeClass: 'c-single' },
  total:  { label: 'Total Power',     tabClass: 'active-total', dotClass: 'c-total', gaugeClass: 'c-total' },
  r:      { label: 'R Phase',         tabClass: 'active-r',     dotClass: 'c-r',     gaugeClass: 'c-r' },
  s:      { label: 'S Phase',         tabClass: 'active-s',     dotClass: 'c-s',     gaugeClass: 'c-s' },
  t:      { label: 'T Phase',         tabClass: 'active-t',     dotClass: 'c-t',     gaugeClass: 'c-t' },
}

const PHASES: PhaseId[] = ['total', 'r', 's', 't']

// ---------------------------------------------------------------------------
// Gauge math (270° arc)
// ---------------------------------------------------------------------------

const G_R = 82
const G_CX = 100
const G_CY = 100
const G_CIRC = 2 * Math.PI * G_R // ~515
const G_ARC_RATIO = 0.75 // 270°
const G_TRACK_DASHOFFSET = G_CIRC * (1 - G_ARC_RATIO)

function powerToDashOffset(watts: number, maxWatts: number): number {
  const ratio = Math.max(0, Math.min(1, watts / maxWatts))
  const arcLen = G_CIRC * G_ARC_RATIO
  return G_CIRC - ratio * arcLen
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Extract a numeric sensor value — handles number, [number], nested sensorData */
function getSensorVal(state: Record<string, unknown> | null, key: string): number | null {
  if (!state) return null
  const extract = (v: unknown): number | null => {
    if (typeof v === 'number') return v
    if (Array.isArray(v) && typeof v[0] === 'number') return v[0]
    if (typeof v === 'string') { const n = parseFloat(v); if (!isNaN(n)) return n }
    return null
  }
  // Try direct key
  const direct = extract(state[key])
  if (direct != null) return direct
  // Try nested sensorData / currentSensorData
  const sd = (state.sensorData ?? state.currentSensorData) as Record<string, unknown> | undefined
  if (sd) {
    const nested = extract(sd[key])
    if (nested != null) return nested
  }
  return null
}

function fmtNumber(n: number | null, decimals = 0): string {
  if (n == null) return '—'
  if (decimals === 0) return n.toLocaleString('en-US', { maximumFractionDigits: 0 })
  return n.toFixed(decimals)
}

/** Detect three-phase from sensorProfile/sensorConfigurable */
function detectThreePhase(device: Record<string, unknown>): boolean {
  // Check capabilities array
  const caps = device.capabilities as Array<Record<string, unknown>> | undefined
  if (Array.isArray(caps)) {
    for (const cap of caps) {
      const attrs = cap.attributes as Record<string, unknown> | undefined
      if (attrs) {
        const configurable = attrs.sensorConfigurable as string[] | undefined
        if (Array.isArray(configurable)) {
          const hasR = configurable.some((k: string) => k.endsWith('_r'))
          const hasS = configurable.some((k: string) => k.endsWith('_s'))
          const hasT = configurable.some((k: string) => k.endsWith('_t'))
          if (hasR && hasS && hasT) return true
        }
        const profile = attrs.sensorProfile as Record<string, unknown> | undefined
        if (profile) {
          const keys = Object.keys(profile)
          const hasR = keys.some(k => k.endsWith('_r'))
          const hasS = keys.some(k => k.endsWith('_s'))
          const hasT = keys.some(k => k.endsWith('_t'))
          if (hasR && hasS && hasT) return true
        }
      }
    }
  }
  // Check top-level attributes
  const attrs = device.attributes as Record<string, unknown> | undefined
  if (attrs) {
    const configurable = attrs.sensorConfigurable as string[] | undefined
    if (Array.isArray(configurable)) {
      return configurable.some((k: string) => k.endsWith('_r')) &&
             configurable.some((k: string) => k.endsWith('_s')) &&
             configurable.some((k: string) => k.endsWith('_t'))
    }
    const profile = attrs.sensorProfile as Record<string, unknown> | undefined
    if (profile) {
      const keys = Object.keys(profile)
      return keys.some(k => k.endsWith('_r')) && keys.some(k => k.endsWith('_s')) && keys.some(k => k.endsWith('_t'))
    }
  }
  return false
}

/** Estimate max wattage from sensorProfile or default */
function getMaxWatts(device: Record<string, unknown>): number {
  const caps = device.capabilities as Array<Record<string, unknown>> | undefined
  if (Array.isArray(caps)) {
    for (const cap of caps) {
      const attrs = cap.attributes as Record<string, unknown> | undefined
      const profile = attrs?.sensorProfile as Record<string, Record<string, unknown>> | undefined
      const rp = profile?.real_power
      if (rp?.Max && typeof rp.Max === 'number') return rp.Max
    }
  }
  return 10000 // default 10kW
}

// ---------------------------------------------------------------------------
// SVG Gradients (shared)
// ---------------------------------------------------------------------------

function GaugeGradients() {
  return (
    <defs>
      <linearGradient id="pmGTotal" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#06b6d4" /><stop offset="100%" stopColor="#38bdf8" />
      </linearGradient>
      <linearGradient id="pmGR" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#ef4444" /><stop offset="100%" stopColor="#f87171" />
      </linearGradient>
      <linearGradient id="pmGS" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#f59e0b" /><stop offset="100%" stopColor="#fbbf24" />
      </linearGradient>
      <linearGradient id="pmGT" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#3b82f6" /><stop offset="100%" stopColor="#60a5fa" />
      </linearGradient>
    </defs>
  )
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function FrostedPowerMeterModal({
  device,
  deviceState,
  deviceType,
  onClose,
  isEditorMode,
  sectionOrder,
  sectionVisible,
  onSectionContextMenu,
  highlightedSection,
  credentials,
}: DeviceModalTemplateProps) {

  const handleContextMenu = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      if (!isEditorMode || !onSectionContextMenu) return
      const target = event.target as HTMLElement | null
      const sectionEl = target?.closest('[data-modal-section]') as HTMLElement | null
      if (!sectionEl) return
      event.preventDefault()
      event.stopPropagation()
      const sectionId = sectionEl.dataset.modalSection || ''
      if (sectionId) onSectionContextMenu(sectionId, { clientX: event.clientX, clientY: event.clientY })
    },
    [isEditorMode, onSectionContextMenu]
  )

  const orderedSections = useMemo(() => {
    const order = sectionOrder?.length ? sectionOrder : DEFAULT_SECTION_ORDER
    const remaining = DEFAULT_SECTION_ORDER.filter((id) => !order.includes(id))
    return [...order, ...remaining]
  }, [sectionOrder])

  const isSectionVisible = useCallback(
    (id: string) => sectionVisible?.[id] !== false,
    [sectionVisible]
  )

  // ---- Settings ----
  const [showSettings, setShowSettings] = useState(false)
  const deviceInfoItems = useMemo(() => {
    const items: Array<{ label: string; value: string; action?: 'copy' }> = []
    items.push({ label: 'DEVICE TYPE', value: deviceType || String(device.type || 'ULTRON_POWER_METER') })
    if ((device as Record<string, unknown>).ip) items.push({ label: 'IP ADDRESS', value: String((device as Record<string, unknown>).ip) })
    if ((device as Record<string, unknown>).fwVersion) items.push({ label: 'FW VERSION', value: String((device as Record<string, unknown>).fwVersion) })
    items.push({ label: 'DEVICE SN', value: device.sn, action: 'copy' })
    return items
  }, [device])
  const [copiedField, setCopiedField] = useState<string | null>(null)
  const copyToClipboard = useCallback((text: string, label: string) => {
    navigator.clipboard.writeText(text).catch(() => {})
    setCopiedField(label); setTimeout(() => setCopiedField(null), 1000)
  }, [])

  // ---- Rename ----
  const [isRenaming, setIsRenaming] = useState(false)
  const [renameValue, setRenameValue] = useState('')
  const [displayName, setDisplayName] = useState(device.name || device.sn)
  const renameInputRef = useRef<HTMLInputElement>(null)
  useEffect(() => { setDisplayName(device.name || device.sn) }, [device.name, device.sn])
  const startRename = useCallback(() => {
    setRenameValue(displayName); setIsRenaming(true)
    setTimeout(() => renameInputRef.current?.focus(), 50)
  }, [displayName])
  const submitRename = useCallback(async () => {
    const trimmed = renameValue.trim()
    if (!trimmed || trimmed === displayName || !credentials) { setIsRenaming(false); return }
    setDisplayName(trimmed); setIsRenaming(false)
    await configureDeviceNicknameWithAuth(credentials, device.sn, trimmed).catch(() => {})
  }, [renameValue, displayName, credentials, device.sn])

  // ---- Device data ----
  const rawDevice = device as Record<string, unknown>
  const isOnline = device.online !== false
  const hasRealDevice = !!(device.sn && !device.sn.startsWith('MOCK'))
  // Default to 3-phase in template preview (no real device) to showcase full UI
  const isThreePhase = useMemo(() => hasRealDevice ? detectThreePhase(rawDevice) : true, [rawDevice, hasRealDevice])
  const maxWatts = useMemo(() => hasRealDevice ? getMaxWatts(rawDevice) : 10000, [rawDevice, hasRealDevice])
  const maxPerPhase = useMemo(() => isThreePhase ? Math.round(maxWatts / 3) : maxWatts, [isThreePhase, maxWatts])

  // ---- Phase state ----
  const [activePhase, setActivePhase] = useState<PhaseId>('total')
  const displayPhase = isThreePhase ? activePhase : 'total'
  const colorKey = isThreePhase ? displayPhase : 'single'
  const phaseColor = PHASE_COLORS[colorKey]
  const keys = PHASE_KEYS[displayPhase]
  const gaugeMax = displayPhase === 'total' ? maxWatts : maxPerPhase

  // ---- Read sensor values (with demo fallback for template preview) ----
  const state = (deviceState ?? null) as Record<string, unknown> | null
  const hasRealData = state != null && Object.keys(state).length > 0

  // Demo data for AppFactory template preview
  const DEMO: Record<PhaseId, { watts: number; volts: number; amps: number }> = {
    total: { watts: 3842, volts: 220, amps: 17.5 },
    r: { watts: 1380, volts: 110, amps: 12.5 },
    s: { watts: 1247, volts: 110, amps: 11.3 },
    t: { watts: 1215, volts: 110, amps: 11.0 },
  }
  const demo = DEMO[displayPhase]

  // For total: try real_power first, fallback to sum of R+S+T
  const rawWatts = getSensorVal(state, keys.power)
  const watts = rawWatts
    ?? (displayPhase === 'total'
      ? (() => { const r = getSensorVal(state, PHASE_KEYS.r.power); const s = getSensorVal(state, PHASE_KEYS.s.power); const t = getSensorVal(state, PHASE_KEYS.t.power); return r != null || s != null || t != null ? (r ?? 0) + (s ?? 0) + (t ?? 0) : null })()
      : null)
    ?? (isOnline ? demo.watts : null)
  const volts = getSensorVal(state, keys.voltage)
    ?? (displayPhase === 'total' ? getSensorVal(state, PHASE_KEYS.r.voltage) : null)
    ?? (isOnline ? demo.volts : null)
  const amps = getSensorVal(state, keys.current)
    ?? (displayPhase === 'total'
      ? (() => { const r = getSensorVal(state, PHASE_KEYS.r.current); const s = getSensorVal(state, PHASE_KEYS.s.current); const t = getSensorVal(state, PHASE_KEYS.t.current); return r != null || s != null || t != null ? (r ?? 0) + (s ?? 0) + (t ?? 0) : null })()
      : null)
    ?? (isOnline ? demo.amps : null)

  // Phase breakdown values (3-phase total view) — with demo fallback
  const rWatts = getSensorVal(state, PHASE_KEYS.r.power) ?? (isOnline ? DEMO.r.watts : null)
  const sWatts = getSensorVal(state, PHASE_KEYS.s.power) ?? (isOnline ? DEMO.s.watts : null)
  const tWatts = getSensorVal(state, PHASE_KEYS.t.power) ?? (isOnline ? DEMO.t.watts : null)
  const rVolts = getSensorVal(state, PHASE_KEYS.r.voltage) ?? (isOnline ? DEMO.r.volts : null)
  const sVolts = getSensorVal(state, PHASE_KEYS.s.voltage) ?? (isOnline ? DEMO.s.volts : null)
  const tVolts = getSensorVal(state, PHASE_KEYS.t.voltage) ?? (isOnline ? DEMO.t.volts : null)
  const rAmps = getSensorVal(state, PHASE_KEYS.r.current) ?? (isOnline ? DEMO.r.amps : null)
  const sAmps = getSensorVal(state, PHASE_KEYS.s.current) ?? (isOnline ? DEMO.s.amps : null)
  const tAmps = getSensorVal(state, PHASE_KEYS.t.current) ?? (isOnline ? DEMO.t.amps : null)

  // Gauge
  const dashOffset = powerToDashOffset(watts ?? 0, gaugeMax)
  const phaseContainerClass = isThreePhase ? `phase-${displayPhase}` : 'phase-single'

  // ---- Render helpers ----
  const sectionHighlight = (id: string) =>
    highlightedSection === id ? { outline: '2px solid rgba(6,182,212,0.6)', outlineOffset: 2 } : undefined

  const mi = (icon: string, extra = '') => (
    <span className={`mi ${extra}`} style={{ fontFamily: "'Material Symbols Rounded'", fontVariationSettings: "'FILL' 1, 'wght' 400" }}>{icon}</span>
  )

  // ---- Render sections ----
  const renderSection = (id: string) => {
    if (!isSectionVisible(id)) return null
    switch (id) {
      case '_modal': return null // background handled by container

      case 'gauge':
        return (
          <div key="gauge" data-modal-section="gauge" style={sectionHighlight('gauge')}>
            <div className="pm__gauge-area">
              <svg className="pm__gauge-svg" viewBox="0 0 200 200">
                <GaugeGradients />
                <circle className="pm__gauge-track" cx={G_CX} cy={G_CY} r={G_R}
                  strokeDasharray={G_CIRC} strokeDashoffset={G_TRACK_DASHOFFSET}
                  transform={`rotate(135 ${G_CX} ${G_CY})`} />
                {isOnline && (
                  <circle className={`pm__gauge-fill ${phaseColor.gaugeClass}`} cx={G_CX} cy={G_CY} r={G_R}
                    strokeDasharray={G_CIRC} strokeDashoffset={dashOffset}
                    transform={`rotate(135 ${G_CX} ${G_CY})`} />
                )}
                <text x="30" y="176" fill="rgba(255,255,255,0.45)" fontSize="9" fontFamily="Inter" fontWeight="500">0</text>
                <text x="148" y="176" fill="rgba(255,255,255,0.45)" fontSize="9" fontFamily="Inter" fontWeight="500">
                  {gaugeMax >= 1000 ? `${Math.round(gaugeMax / 1000)}kW` : `${gaugeMax}W`}
                </text>
              </svg>
              <div className="pm__gauge-center">
                <div className="pm__gauge-value">{isOnline ? fmtNumber(watts) : '—'}</div>
                <div className="pm__gauge-unit">WATTS</div>
                <div className={`pm__gauge-label ${phaseColor.gaugeClass}`}>{phaseColor.label}</div>
              </div>
            </div>
          </div>
        )

      case 'metrics':
        // Total view hides V/A/PF — Phase Breakdown card shows per-phase instead
        if (isThreePhase && displayPhase === 'total') return null
        return (
          <div key="metrics" data-modal-section="metrics" style={sectionHighlight('metrics')}>
            <div className="pm__metrics">
              <div className="pm__metric">
                <div className={`pm__metric-icon ${phaseColor.gaugeClass}`}>{mi('electric_bolt')}</div>
                <div className="pm__metric-value">{fmtNumber(volts)}<span className="pm__metric-unit">V</span></div>
                <div className="pm__metric-label">Voltage</div>
              </div>
              <div className="pm__metric">
                <div className={`pm__metric-icon ${phaseColor.gaugeClass}`}>{mi('water')}</div>
                <div className="pm__metric-value">{fmtNumber(amps, 1)}<span className="pm__metric-unit">A</span></div>
                <div className="pm__metric-label">Current</div>
              </div>
              <div className="pm__metric">
                <div className={`pm__metric-icon ${phaseColor.gaugeClass}`}>{mi('speed')}</div>
                <div className="pm__metric-value">
                  {isOnline && volts && amps && watts ? (watts / (volts * amps)).toFixed(2) : '—'}
                </div>
                <div className="pm__metric-label">PF</div>
              </div>
            </div>
          </div>
        )

      case 'phases':
        if (!isThreePhase || displayPhase !== 'total') return null
        return (
          <div key="phases" data-modal-section="phases" style={sectionHighlight('phases')}>
            <div className="pm__phases-overview">
              <div className="pm__phases-title">{mi('account_tree')} Phase Breakdown</div>
              <div className="pm__phases-grid">
                {(['r', 's', 't'] as PhaseId[]).map((p) => {
                  const pw = p === 'r' ? rWatts : p === 's' ? sWatts : tWatts
                  const pv = p === 'r' ? rVolts : p === 's' ? sVolts : tVolts
                  const pa = p === 'r' ? rAmps : p === 's' ? sAmps : tAmps
                  const pct = pw != null && maxPerPhase > 0 ? Math.round((pw / maxPerPhase) * 100) : 0
                  return (
                    <div key={p} className={`pm__phase-card ${p}`} onClick={() => setActivePhase(p)}>
                      <div className="pm__phase-card-label">{p.toUpperCase()}</div>
                      <div className="pm__phase-card-power">{fmtNumber(pw)}<span className="pm__phase-card-unit"> W</span></div>
                      <div className="pm__phase-card-sub">{fmtNumber(pv)}V · {fmtNumber(pa, 1)}A</div>
                      <div className="pm__phase-bar-track"><div className="pm__phase-bar-fill" style={{ width: `${Math.min(100, pct)}%` }} /></div>
                    </div>
                  )
                })}
              </div>
            </div>
          </div>
        )

      case 'energy': {
        // Demo energy values per phase
        const DEMO_ENERGY: Record<PhaseId, { today: number; month: number }> = {
          total: { today: 28.6, month: 612 },
          r: { today: 10.2, month: 218 },
          s: { today: 9.1, month: 196 },
          t: { today: 9.3, month: 198 },
        }
        const demoE = DEMO_ENERGY[displayPhase]
        const energyToday = getSensorVal(state, 'energy_today') ?? (isOnline ? demoE.today : null)
        const energyMonth = getSensorVal(state, 'energy_month') ?? (isOnline ? demoE.month : null)
        return (
          <div key="energy" data-modal-section="energy" style={sectionHighlight('energy')}>
            <div className="pm__energy-card">
              <div className="pm__energy-title">
                {mi('energy_savings_leaf', phaseColor.gaugeClass)}
                {' '}Energy{isThreePhase ? ` (${displayPhase === 'total' ? 'Total' : `${displayPhase.toUpperCase()} Phase`})` : ''}
              </div>
              <div className="pm__energy-row">
                <div className="pm__energy-item">
                  <div className="pm__energy-item-value">{fmtNumber(energyToday, 1)}<span className="pm__energy-item-unit"> kWh</span></div>
                  <div className="pm__energy-item-label">Today</div>
                </div>
                <div className="pm__energy-divider" />
                <div className="pm__energy-item">
                  <div className="pm__energy-item-value">{fmtNumber(energyMonth)}<span className="pm__energy-item-unit"> kWh</span></div>
                  <div className="pm__energy-item-label">This Month</div>
                </div>
              </div>
            </div>
          </div>
        )
      }

      case 'chart': {
        // Demo SVG chart paths per phase (different wave shapes)
        const CHART_PATHS: Record<PhaseId, string> = {
          total: 'M0,55 L20,52 L40,48 L60,50 L80,42 L100,38 L120,35 L140,30 L160,22 L180,18 L200,20 L220,25 L240,22 L260,15 L280,18 L300,20 L313,16',
          r: 'M0,50 L20,48 L40,45 L60,48 L80,40 L100,35 L120,30 L140,28 L160,20 L180,15 L200,18 L220,22 L240,20 L260,12 L280,15 L300,18 L313,14',
          s: 'M0,52 L20,50 L40,48 L60,52 L80,45 L100,40 L120,38 L140,35 L160,28 L180,22 L200,25 L220,30 L240,28 L260,20 L280,22 L300,25 L313,20',
          t: 'M0,54 L20,52 L40,50 L60,54 L80,48 L100,42 L120,40 L140,38 L160,30 L180,25 L200,28 L220,32 L240,30 L260,22 L280,25 L300,28 L313,22',
        }
        const chartPath = CHART_PATHS[displayPhase]
        const areaPath = `${chartPath} L313,70 L0,70 Z`
        const gradId = `pmChartGrad_${displayPhase}`
        const areaGradId = `pmChartArea_${displayPhase}`
        const GRAD_COLORS: Record<PhaseId, [string, string]> = {
          total: ['#06b6d4', '#38bdf8'], r: ['#ef4444', '#f87171'], s: ['#f59e0b', '#fbbf24'], t: ['#3b82f6', '#60a5fa'],
        }
        const [c1, c2] = GRAD_COLORS[displayPhase]
        return (
          <div key="chart" data-modal-section="chart" style={sectionHighlight('chart')}>
            <div className="pm__chart-card">
              <div className="pm__chart-header">
                <div className="pm__chart-title">
                  {mi('show_chart', phaseColor.gaugeClass)}
                  {displayPhase === 'total' ? ' Total Power' : ` ${displayPhase.toUpperCase()} Phase Power`}
                </div>
                <div className={`pm__chart-period ${phaseColor.gaugeClass}`}>24h</div>
              </div>
              <div className="pm__chart-area">
                <svg className="pm__chart-svg" viewBox="0 0 313 70" preserveAspectRatio="none">
                  <defs>
                    <linearGradient id={gradId} x1="0%" y1="0%" x2="100%" y2="0%">
                      <stop offset="0%" stopColor={c1} stopOpacity="0.5" /><stop offset="100%" stopColor={c2} />
                    </linearGradient>
                    <linearGradient id={areaGradId} x1="0%" y1="0%" x2="0%" y2="100%">
                      <stop offset="0%" stopColor={c1} stopOpacity="0.15" /><stop offset="100%" stopColor={c1} stopOpacity="0" />
                    </linearGradient>
                  </defs>
                  <line className="pm__chart-grid" x1="0" y1="17" x2="313" y2="17" />
                  <line className="pm__chart-grid" x1="0" y1="35" x2="313" y2="35" />
                  <line className="pm__chart-grid" x1="0" y1="52" x2="313" y2="52" />
                  <path d={areaPath} fill={`url(#${areaGradId})`} />
                  <path d={chartPath} fill="none" stroke={`url(#${gradId})`} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <div className="pm__chart-labels">
                <span className="pm__chart-label">00:00</span>
                <span className="pm__chart-label">06:00</span>
                <span className="pm__chart-label">12:00</span>
                <span className="pm__chart-label">18:00</span>
                <span className="pm__chart-label">Now</span>
              </div>
            </div>
          </div>
        )
      }

      default: return null
    }
  }

  return (
    <div
      className={`pm-frosted ${phaseContainerClass}${!isOnline ? ' offline' : ''}`}
      onContextMenu={handleContextMenu}
    >
      {/* Scrollable content — frosted glass stays fixed, elements scroll */}
      <div className="pm-frosted__scroll">
        {/* Header */}
        <div className="pm__header">
          <button className="pm__back" onClick={onClose}>&lsaquo;</button>
          <div className="pm__title">{displayName}</div>
        </div>
        <div className={`pm__subtitle ${isOnline ? 'online' : 'offline'}`}>
          {isOnline
            ? (isThreePhase ? '3-Phase · Real-time' : 'Monitoring · Real-time')
            : 'Offline'}
        </div>

        {/* Phase tabs (3-phase only) */}
        {isThreePhase && (
          <>
            <div className="pm__phase-tabs">
              {PHASES.map((p) => (
                <button
                  key={p}
                  className={`pm__phase-tab ${activePhase === p ? PHASE_COLORS[p].tabClass : ''}`}
                  onClick={() => setActivePhase(p)}
                >
                  {p === 'total' ? '總表' : p.toUpperCase()}
                </button>
              ))}
            </div>
            <div className="pm__phase-dots">
              {PHASES.map((p) => (
                <span key={p} className={`pm__phase-dot ${activePhase === p ? `active ${PHASE_COLORS[p].dotClass}` : ''}`} />
              ))}
            </div>
          </>
        )}

        {/* Ordered sections */}
        {orderedSections.map(renderSection)}

        {/* Swipe hint (R/S/T views only) */}
        {isThreePhase && displayPhase !== 'total' && (
          <div className="pm__swipe-hint">{mi('swipe_left')} Swipe to switch phase</div>
        )}

        {/* Settings */}
        <button className="pm__settings-btn" onClick={() => setShowSettings(true)}>{mi('settings')}</button>
      </div>

      {/* Settings Overlay */}
      {showSettings && (
        <div className="pm__settings-overlay" onClick={() => setShowSettings(false)}>
          <div className="pm__settings-sheet" onClick={e => e.stopPropagation()}>
            <div className="pm__settings-header">
              <span className="pm__settings-title">設定</span>
              <button className="pm__settings-close" onClick={() => setShowSettings(false)}>✕</button>
            </div>
            <div className="pm__settings-section">
              <div className="pm__settings-section-title">裝置名稱</div>
              {isRenaming ? (
                <div className="pm__settings-row">
                  <input ref={renameInputRef} className="pm__rename-input" value={renameValue}
                    onChange={e => setRenameValue(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') void submitRename(); if (e.key === 'Escape') setIsRenaming(false) }}
                    onBlur={() => void submitRename()} maxLength={40} />
                </div>
              ) : (
                <div className="pm__settings-row">
                  <span className="pm__settings-value" style={{ textAlign: 'left', flex: 1 }}>{displayName}</span>
                  <button className="pm__settings-edit" onClick={startRename} title="Rename">
                    {mi('edit')}
                  </button>
                </div>
              )}
            </div>
            <div className="pm__settings-section">
              <div className="pm__settings-section-title">裝置資訊</div>
              {deviceInfoItems.map(item => (
                <div key={item.label} className="pm__settings-row">
                  <span className="pm__settings-label">{item.label}</span>
                  <span className="pm__settings-value">{item.value}</span>
                  {item.action === 'copy' && (
                    <button className="pm__settings-copy" onClick={() => copyToClipboard(item.value, item.label)} title="Copy">
                      📋{copiedField === item.label && <span className="pm__copied-toast">Copied</span>}
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
